"use client";

import { Typography, Button, Popover, Avatar } from "antd/lib";
import Image from "next/image";
import { useLanguageContext } from "@/context/LanguageContext";
import { usePrice } from "@/context/PriceContext";
import { useState, useMemo, useCallback, useEffect } from "react";
import { SearchOutlined, DownOutlined, BellFilled, StarFilled, SwapOutlined } from "@ant-design/icons";
import bybitIcon from "@/asset/images/chains/bybit.svg";
import coinbaseIcon from "@/asset/images/chains/coinbase.svg";
import binanceIcon from "@/asset/images/chains/binance.svg";
import debounce from "lodash/debounce";
import PriceChange from "../PriceChange";
import type { TokenData } from "@/models/token";
import { formatThousands } from "@/helpers";
import { useCurrencyContext } from "@/context/CurrencyContext";
import useMyQuery from "@/hooks/useMyQuery";

interface FiatCurrency {
  id: string;
  symbol: string;
  name: string;
  price: number;
  isFiat: boolean;
}

interface CurrencyRate {
  code: string;
  value: number;
}

interface CurrencyResponse {
  success: boolean;
  data: Record<string, CurrencyRate>;
}

export default function ExchangeCard() {
  const { language } = useLanguageContext();
  const { currentCurrency } = useCurrencyContext();
  const { crypto } = usePrice();
  const [searchValue, setSearchValue] = useState("");
  const [selectedFrom, setSelectedFrom] = useState(crypto.find(c => c.symbol === "USDT"));
  const [selectedTo, setSelectedTo] = useState(crypto.find(c => c.symbol === "BTC"));

  // Add states for input values
  const [fromValue, setFromValue] = useState("47500");
  const [toValue, setToValue] = useState("1");

  const defaultCryptos = useMemo(
    () => crypto.filter(coin => ["BTC", "ETH", "XRP", "USDT"].includes(coin.symbol)),
    [crypto],
  );

  const { data: currencyRates } = useMyQuery<CurrencyResponse>({
    url: "/v1/currency/rates",
  });

  const fiatCurrencies: FiatCurrency[] = useMemo(() => {
    if (!currencyRates?.data) return [];

    return Object.entries(currencyRates.data).map(([symbol, rate]) => ({
      id: symbol,
      symbol,
      name: symbol,
      price: rate.value,
      isFiat: true,
    }));
  }, [currencyRates]);

  const filteredCryptos = useMemo(() => {
    if (!searchValue) return defaultCryptos;

    const searchLower = searchValue.toLowerCase();
    return crypto
      .filter(coin => coin.symbol.toLowerCase().includes(searchLower) || coin.name.toLowerCase().includes(searchLower))
      .slice(0, 20);
  }, [searchValue, crypto, defaultCryptos]);

  const handleSearch = useCallback(
    debounce((value: string) => {
      setSearchValue(value);
    }, 300),
    [],
  );

  const calculateExchangeRate = useCallback((amount: number, from: number, to: number) => {
    if (!amount || !from || !to) return 0;
    return (amount * from) / to;
  }, []);

  const handleFromChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setFromValue(value);

    if (value && selectedFrom?.price && selectedTo?.price) {
      const numValue = parseFloat(value.replace(/,/g, ""));
      if (!isNaN(numValue)) {
        const newToValue = (numValue * selectedFrom.price) / selectedTo.price;
        setToValue(newToValue.toFixed(8).replace(/\.?0+$/, ""));
      }
    }
  };

  const handleToChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setToValue(value);

    if (value && selectedFrom?.price && selectedTo?.price) {
      const numValue = parseFloat(value.replace(/,/g, ""));
      if (!isNaN(numValue)) {
        const newFromValue = (numValue * selectedTo.price) / selectedFrom.price;
        setFromValue(newFromValue.toFixed(2).replace(/\.?0+$/, ""));
      }
    }
  };

  useEffect(() => {
    if (!selectedFrom?.price || !selectedTo?.price) return;

    setToValue("1");
    const newFromValue = calculateExchangeRate(1, selectedTo.price, selectedFrom.price);
    if (!isNaN(newFromValue)) {
      setFromValue(newFromValue.toFixed(2).replace(/\.?0+$/, ""));
    }
  }, [calculateExchangeRate, selectedFrom, selectedTo]);

  const handleSwap = () => {
    const tempFrom = selectedFrom;
    const tempTo = selectedTo;
    setSelectedFrom(tempTo);
    setSelectedTo(tempFrom);
  };

  const searchContent = (onSelect: (coin: TokenData) => void, selectedCoin?: TokenData, otherSelected?: TokenData) => (
    <div className="w-[320px] max-h-[400px] overflow-auto">
      <div className="mb-2">
        <div className="flex items-center gap-2 bg-[#F8F9FA] rounded-lg px-4 h-[42px]">
          <SearchOutlined className="text-gray-400" />
          <input
            type="text"
            placeholder="Search for Coins and Currencies"
            className="bg-transparent outline-none w-full text-sm"
            onChange={e => handleSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <div className="text-base font-medium px-2 mb-1">Cryptocurrencies</div>
          {filteredCryptos.map(coin => (
            <div
              key={coin.symbol}
              className={`flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer ${
                selectedCoin?.symbol === coin.symbol ? "bg-gray-50" : ""
              } ${otherSelected?.symbol === coin.symbol ? "opacity-50 cursor-not-allowed" : ""}`}
              onClick={() => otherSelected?.symbol !== coin.symbol && onSelect(coin)}
            >
              <div className="flex-1 flex gap-2 items-center">
                <Avatar src={coin.logo_uri} size={24} />
                <div>
                  <Typography.Text strong>{coin.token_symbol}</Typography.Text>
                  <Typography.Text type="secondary" className="ml-1">
                    {coin.symbol}
                  </Typography.Text>
                </div>
              </div>
              <Typography.Text type="secondary">${coin.price.toLocaleString()}</Typography.Text>
            </div>
          ))}
          {filteredCryptos.length === 0 && <div className="text-center text-gray-500 py-4">No results found</div>}
        </div>

        <div>
          <div className="text-base font-medium px-2 mb-1">Fiat Currencies</div>
          {fiatCurrencies.map(coin => {
            // Convert FiatCurrency to TokenData
            const fiatToken: TokenData = {
              id: coin.symbol,
              _id: coin.symbol,
              symbol: coin.symbol,
              name: coin.name,
              price: coin.price,
              logo_uri: `/images/fiat/${coin.symbol.toLowerCase()}.svg`,
              volume: 0,
              token_symbol: coin.symbol,
              stable_coin: true,
              market_cap: 0,
              price_change: {
                change_1D: { usd: 0, percentage: 0 },
                change_1H: { usd: 0, percentage: 0 },
                change_4H: { usd: 0, percentage: 0 },
                change_1W: { usd: 0, percentage: 0 },
                change_1M: { usd: 0, percentage: 0 },
                change_3M: { usd: 0, percentage: 0 },
                change_6M: { usd: 0, percentage: 0 },
                change_1Y: { usd: 0, percentage: 0 },
                change_YTD: { usd: 0, percentage: 0 },
              },
              rank_label: "",
              is_flag: false,
              narrative: [],
              price_change_duration: 0,
            };

            return (
              <div
                key={coin.symbol}
                className={`flex items-center gap-3 p-2 hover:bg-gray-50 cursor-pointer ${
                  selectedCoin?.symbol === coin.symbol ? "bg-gray-50" : ""
                } ${otherSelected?.symbol === coin.symbol ? "opacity-50 cursor-not-allowed" : ""}`}
                onClick={() => otherSelected?.symbol !== coin.symbol && onSelect(fiatToken)}
              >
                <div className="flex-1">
                  <Typography.Text strong>{coin.name}</Typography.Text>
                  <Typography.Text type="secondary" className="ml-1">
                    {coin.symbol}
                  </Typography.Text>
                </div>
                <Typography.Text type="secondary">${coin.price.toLocaleString()}</Typography.Text>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200">
      <div
        className="flex items-center gap-3 mb-6 cursor-pointer"
        onClick={() => window.open(`/${language}/markets/${selectedTo?.symbol}`, "_blank")}
      >
        <Avatar src={selectedTo?.logo_uri} size={40} />
        <div>
          <Typography.Title level={4} className="!mb-0">
            {selectedTo?.name}
          </Typography.Title>
          <Typography.Text className="text-gray-500">{selectedTo?.symbol}</Typography.Text>
        </div>
        <div className="ml-auto text-right">
          <Typography.Title level={4} className="!mb-0">
            ${selectedTo?.price?.toLocaleString() || "0.00"}
          </Typography.Title>
          <PriceChange price={selectedTo?.price_change?.change_1D?.percentage ?? 0} />
        </div>
      </div>

      <div className="space-y-2">
        <div>
          {/* <Typography.Text className="text-gray-500 mb-2 block">From</Typography.Text> */}
          <div className="flex items-center bg-white rounded-lg h-[42px] border border-gray-200">
            <input
              type="text"
              value={toValue}
              onChange={handleToChange}
              className="bg-transparent outline-none w-full px-4 h-full"
            />
            <div className="h-[20px] w-[1px] bg-gray-200 mx-2" />
            <Popover
              content={searchContent(coin => setSelectedTo(coin), selectedTo, selectedFrom)}
              trigger="click"
              placement="bottomRight"
            >
              <div className="flex items-center gap-2 px-3 cursor-pointer hover:bg-gray-50 h-full min-w-[120px] justify-between">
                <div className="flex items-center gap-2">
                  <Avatar src={selectedTo?.logo_uri} size={24} />
                  <Typography.Text>{selectedTo?.symbol}</Typography.Text>
                </div>
                <DownOutlined className="text-xs text-gray-400" />
              </div>
            </Popover>
          </div>
        </div>

        {/* Swap button */}
        <div className="flex justify-center">
          <Button
            type="text"
            shape="circle"
            icon={<SwapOutlined className="text-xl" />}
            onClick={handleSwap}
            className="flex items-center justify-center h-8 w-8 bg-gray-100 hover:bg-gray-200"
          />
        </div>

        <div>
          {/* <Typography.Text className="text-gray-500 mb-2 block">To</Typography.Text> */}
          <div className="flex items-center bg-white rounded-lg h-[42px] border border-gray-200">
            <input
              type="text"
              value={fromValue}
              onChange={handleFromChange}
              className="bg-transparent outline-none w-full px-4 h-full"
            />
            <div className="h-[20px] w-[1px] bg-gray-200 mx-2" />
            <Popover
              content={searchContent(coin => setSelectedFrom(coin), selectedFrom, selectedTo)}
              trigger="click"
              placement="bottomRight"
            >
              <div className="flex items-center gap-2 px-3 cursor-pointer hover:bg-gray-50 h-full min-w-[120px] justify-between">
                <div className="flex items-center gap-2">
                  <Avatar src={selectedFrom?.logo_uri} size={24} />
                  <Typography.Text>{selectedFrom?.symbol}</Typography.Text>
                </div>
                <DownOutlined className="text-xs text-gray-400" />
              </div>
            </Popover>
          </div>
        </div>

        <Typography.Text className="text-gray-500 block">
          1 {selectedTo?.symbol} ={" "}
          {selectedTo?.price && selectedFrom?.price ? (
            <>
              {formatThousands(selectedTo.price / selectedFrom.price, currentCurrency)} {selectedFrom?.symbol}
            </>
          ) : (
            <>0.00 {selectedFrom?.symbol}</>
          )}
        </Typography.Text>

        <Button
          block
          className="flex items-center justify-center gap-2 h-12 "
          href={`https://www.bybit.com/trade/spot/${language}`}
          target="_blank"
        >
          <Image src={binanceIcon} alt="spot" width={24} height={24} className="rounded-full" />
          Trade Spot
        </Button>

        <Button
          block
          className="flex items-center justify-center gap-2 h-12"
          href={`https://www.bybit.com/trade/usdt/${language}`}
          target="_blank"
        >
          <Image src={bybitIcon} alt="future" width={24} height={24} className="rounded-full" />
          Trade Future
        </Button>

        <Button
          block
          className="flex items-center justify-center gap-2 h-12"
          href={`https://www.bybit.com/trade/spot/${language}`}
          target="_blank"
        >
          <Image src={coinbaseIcon} alt="now" width={24} height={24} className="rounded-full" />
          Trade Now
        </Button>

        <div className="grid grid-cols-2 gap-4">
          <Button block icon={<BellFilled className="text-black" />}>
            Create Alert
          </Button>
          <Button block icon={<StarFilled className="text-black" />}>
            Add to watchlist
          </Button>
        </div>
      </div>
    </div>
  );
}
